import { useMemo } from "react";
import { makeQueryString } from "core/makeQueryString";
import { dateFormat } from "core/datetime";
import { useFetchGet } from "hooks/fetch/useFetchGet";
import { AlertOccurrenceActioned } from "interfaces/alert";

export interface GetArchivedAlertsResponse {
  loading: boolean;
  error: boolean;
  alerts: AlertOccurrenceActioned[];
  count: number;
  refetchAlerts(): Promise<AlertOccurrenceActioned[]>;
}

interface ArchivedAlertsFetchResponse {
  loading: boolean;
  error: boolean;
  response: { results: AlertOccurrenceActioned[]; count: number } | undefined;
  refetch(): Promise<AlertOccurrenceActioned[]>;
}

/**
 * Hook for getting a list of archived alerts from the AI Hub API
 * @param page the current page
 * @param pageSize the current page size
 * @param severity the id of the severity type to filter to
 * @param siteId the id of the site to filter to
 * @param machineId the id of the machine to filter to
 * @param startDate the date to use as a start date for filtering
 * @param endDate the date to use as an end date for filtering
 * @param sortBy the fields to sort by
 * @param isSortByAscending whether to sort ascending
 */
export const useGetArchivedAlerts = (
  page: number = 1,
  pageSize: number = 10,
  severity: number | undefined = undefined,
  siteId: number | undefined = undefined,
  machineId: number | undefined = undefined,
  startDate: Date | undefined = undefined,
  endDate: Date | undefined = undefined,
  sortByFields: { id: string; desc: boolean }[] | undefined = undefined
): GetArchivedAlertsResponse => {
  const url = useMemo(() => {
    return (
      `alert_occurrences_actioned` +
      makeQueryString({
        page,
        size: pageSize,
        site_id: siteId,
        severity_type_id__in: severity,
        alert__machine_id__in: machineId,
        actioned_date__gte: startDate && dateFormat(startDate),
        actioned_date__lte: endDate && dateFormat(endDate),
        ordering: getNewOrderingString(sortByFields)
      })
    );
  }, [
    page,
    pageSize,
    siteId,
    severity,
    machineId,
    startDate,
    endDate,
    sortByFields
  ]);

  const {
    loading,
    error,
    response = { results: [], count: 0 },
    refetch
  }: ArchivedAlertsFetchResponse = useFetchGet(url);

  return {
    loading,
    error,
    alerts: response.results,
    count: response.count,
    refetchAlerts: refetch
  };
};

export const getNewOrderingString = (
  sortByFields: { id: string; desc: boolean }[] | undefined
): string | [] => {
  if (!sortByFields || sortByFields.length === 0) {
    return [];
  }

  return sortByFields
    .flatMap(field => {
      return field;
    })
    .map(field => (field.desc ? `-${field.id}` : field.id)) // Column sorting direction is reversed in aihub by choice
    .join(",");
};
